export const CONTENT_TYPE_JSON = 'application/json';

const baseHeaders = {
  'Accept-Encoding': 'deflate',
  'Content-Type': 'application/json',
  Accept: 'application/json'
};

const HttpOk = 200;
const HttpMultipleChoices = 300;
const HttpNoContent = 204;

const isSuccessStatus = (status: number) => status >= HttpOk && status < HttpMultipleChoices;

/** Rejection of an api call: the http status (0 when the request never reached the api) and the parsed body. */
export class ApiError extends Error {
  status: number;
  body?: unknown;

  constructor(status: number, body?: unknown) {
    super(`Api call failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

const authorizationHeader = (): { Authorization?: string } => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const checkHttpStatus = (response: Response) => {
  const { status } = response;
  if (isSuccessStatus(status)) {
    return response;
  }
  return Promise.reject({
    status
  });
};

const toUrlParams = (params: { [key: string]: any }) =>
  Object.keys(params)
    .map((key) => ({ key, value: params[key] }))
    .filter(({ value }) => value !== null && value !== undefined)
    .reduce((acc, param) => {
      acc.append(param.key, param.value);
      return acc;
    }, new URLSearchParams())
    .toString();

const apiCall = (method: string) => (baseUrl: string, arg2?: any, arg3?: any) => {
  let url = baseUrl;
  let headers;
  let body;
  if (method === 'GET' || method === 'DELETE') {
    const options = arg2 || {};
    headers = { ...baseHeaders, ...options.headers };
    if (options.params) {
      url += `?${toUrlParams(options.params)}`;
    }
  } else {
    body = arg2;
    headers = { ...baseHeaders, ...arg3 };
  }

  if (body) {
    if (body instanceof FormData) {
      delete headers['Content-Type'];
    } else {
      body = JSON.stringify(body);
    }
  }

  headers = { ...headers, ...authorizationHeader() };
  return fetch(url, {
    method,
    headers,
    body
  })
    .then(checkHttpStatus)
    .then((response: any) => {
      const contentType = response.headers.get('Content-Type') ?? '';
      const contentLength = response.headers.get('Content-Length');
      if (contentType.includes('zip')) {
        return response;
      } else if (contentType.includes('image')) {
        return response;
      } else if (contentType.includes('csv')) {
        return response.blob();
      } else if (
        response.status !== HttpNoContent &&
        (contentLength === null || (contentLength && Number(contentLength) !== 0))
      ) {
        return response.json();
      }
      return Promise.resolve();
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

const apiGet = apiCall('GET');

const apiPost = apiCall('POST');

const apiPut = apiCall('PUT');

const apiPatch = apiCall('PATCH');

const apiDelete = apiCall('DELETE');

const PERCENT = 100;

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

// Multipart upload with progress. fetch gives no upload progress, hence XMLHttpRequest.
// Rejects like the other calls, with the api error body added when it could be parsed
// (the api answers errors with { message: string | string[] }).
const apiUpload = <T>(url: string, formData: FormData, onProgress?: (percent: number) => void): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Accept', CONTENT_TYPE_JSON);
    const { Authorization } = authorizationHeader();
    if (Authorization) {
      xhr.setRequestHeader('Authorization', Authorization);
    }

    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(Math.round((event.loaded / event.total) * PERCENT));
        }
      };
    }

    xhr.onload = () => {
      const body = parseJson(xhr.responseText);
      if (isSuccessStatus(xhr.status)) {
        resolve((body ?? {}) as T);
      } else {
        reject(new ApiError(xhr.status, body));
      }
    };
    xhr.onerror = () => reject(new ApiError(0));
    xhr.onabort = () => reject(new ApiError(0));

    xhr.send(formData);
  });

export const apiUtils = {
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  apiUpload
};
